import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Wrench,
  ClipboardCheck,
  Disc3,
  CircleDot,
  ScanSearch,
  Star,
  Phone,
  Mail,
  Check,
  Clock,
} from "lucide-react";

const NAV = [
  { label: "Ydelser", href: "#ax-ydelser" },
  { label: "Hvorfor os", href: "#ax-hvorfor" },
  { label: "Værkstedet", href: "#ax-vaerksted" },
  { label: "Anmeldelser", href: "#ax-anmeldelser" },
  { label: "Kontakt", href: "#ax-kontakt" },
];

const SERVICES = [
  { icon: ClipboardCheck, title: "Serviceeftersyn", text: "Eftersyn efter fabrikantens forskrifter — med stempel i servicebogen." },
  { icon: Disc3, title: "Bremser", text: "Kontrol og udskiftning af klodser, skiver og væske for din sikkerhed." },
  { icon: CircleDot, title: "Dæk & hjul", text: "Dækskift, afbalancering og opbevaring af sommer- og vinterdæk." },
  { icon: ScanSearch, title: "Fejlfinding", text: "Moderne diagnoseudstyr finder hurtigt årsagen til lys i instrumentet." },
  { icon: Wrench, title: "Reparation", text: "Fra undervogn til motor — vi reparerer alle mærker med kvalitetsdele." },
];

const WHY = [
  "Ærlig rådgivning og fair priser",
  "Erfarne mekanikere på huset",
  "Fast pris, før arbejdet går i gang",
  "Alle bilmærker — nye som gamle",
];

const REVIEWS = [
  { name: "Henrik J.", text: "Hurtig diagnose og en pris, der holdt. Min bil kører som ny igen." },
  { name: "Camilla N.", text: "Fik en ærlig vurdering i stedet for en dyr reparation. Det skaber tillid." },
  { name: "Thomas B.", text: "Dækskift og eftersyn på samme dag. Nemt, professionelt og til tiden." },
];

const HOURS = [
  ["Mandag – torsdag", "7.30 – 17.00"],
  ["Fredag", "7.30 – 15.30"],
  ["Lørdag – søndag", "Lukket"],
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const Label = ({ children }) => (
  <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.25em] text-red-500 mb-4">
    {children}
  </p>
);

export default function ApexAutoCase({ onNavigate }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    onNavigate?.(href);
  };

  return (
    <div className="bg-zinc-950 text-zinc-100 antialiased min-h-screen" data-testid="apex-case-page">
      <div className="bg-white text-zinc-900 text-xs sm:text-sm py-2.5 px-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
        <span className="font-medium">Konceptprojekt – ikke en rigtig kunde. Designet af Naveli.</span>
        <Link to="/" data-testid="apex-back-link" className="inline-flex items-center gap-1 font-semibold underline underline-offset-2">
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
          Tilbage til Naveli
        </Link>
      </div>

      <header className="sticky top-0 z-50 bg-zinc-950/85 backdrop-blur-md border-b border-zinc-800/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
          <a href="#ax-top" onClick={go("#ax-top")} data-testid="apex-logo" className="flex items-center gap-2.5 font-bold tracking-[0.12em] text-sm">
            <span className="w-8 h-8 rounded-lg bg-red-600 text-white flex items-center justify-center">
              <Wrench className="w-4 h-4" aria-hidden="true" />
            </span>
            APEX AUTO
          </a>
          <nav className="hidden md:flex items-center gap-7" aria-label="Apex Auto menu">
            {NAV.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={go(l.href)}
                data-testid={`apex-nav-${l.href.replace("#ax-", "")}`}
                className="text-sm text-zinc-400 hover:text-white transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="#ax-kontakt"
            onClick={go("#ax-kontakt")}
            data-testid="apex-book-cta"
            className="inline-flex items-center gap-2 bg-red-600 text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-red-500 hover:-translate-y-px transition-all duration-200"
          >
            Book tid
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </header>

      <section id="ax-top" className="relative min-h-[80vh] flex items-center overflow-hidden" data-testid="apex-hero">
        <img
          src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          alt="Bilmotor på værkstedet hos Apex Auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/75 to-zinc-950/30" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-24">
          <motion.p {...fadeUp()} className="font-mono-accent text-xs uppercase tracking-[0.3em] text-red-500 mb-6">
            Autoværksted · København
          </motion.p>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight leading-[1.06] max-w-3xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
                Dit lokale værksted.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
                Ærlig service.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                Professionelt arbejde.
              </motion.span>
            </span>
          </h1>
          <motion.p {...fadeUp(0.6)} className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
            Service, reparation og fejlfinding på alle bilmærker — med fast pris,
            før vi går i gang.
          </motion.p>
          <motion.div {...fadeUp(0.75)} className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href="#ax-kontakt"
              onClick={go("#ax-kontakt")}
              data-testid="apex-hero-book-button"
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white font-semibold rounded-lg px-8 py-4 hover:bg-red-500 hover:-translate-y-0.5 transition-all duration-200"
            >
              Book tid
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
            <a
              href="#ax-kontakt"
              onClick={go("#ax-kontakt")}
              data-testid="apex-hero-quote-button"
              className="inline-flex items-center justify-center gap-2 border border-zinc-600 text-white font-medium rounded-lg px-8 py-4 hover:border-white hover:-translate-y-0.5 transition-all duration-200"
            >
              Få et tilbud
            </a>
          </motion.div>
          <motion.div {...fadeUp(0.9)} className="mt-10 flex items-center gap-2 text-sm text-zinc-400">
            <Clock className="w-4 h-4 text-red-500" aria-hidden="true" />
            Man–Tor 7.30–17.00 · Fre 7.30–15.30
          </motion.div>
        </div>
      </section>

      <section id="ax-ydelser" className="py-20 sm:py-28" data-testid="apex-services">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()}>
            <Label>Ydelser</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-xl">
              Alt din bil har brug for — ét sted
            </h2>
          </motion.div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp(i * 0.07)}
                data-testid={`apex-service-${i + 1}`}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-7 hover:border-red-600 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-lg bg-red-600/15 text-red-500 flex items-center justify-center mb-5">
                  <s.icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold">{s.title}</h3>
                <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
            <motion.a
              {...fadeUp(0.35)}
              href="#ax-kontakt"
              onClick={go("#ax-kontakt")}
              data-testid="apex-services-cta"
              className="bg-red-600 rounded-xl p-7 flex flex-col justify-between hover:bg-red-500 hover:-translate-y-1 transition-all duration-300 min-h-[180px]"
            >
              <p className="text-lg font-semibold leading-snug">
                Mangler din bil service eller syn?
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold">
                Book tid i dag
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </span>
            </motion.a>
          </div>
        </div>
      </section>

      <section id="ax-hvorfor" className="py-20 sm:py-28 bg-zinc-900/50" data-testid="apex-why">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid gap-12 lg:grid-cols-2 items-center">
          <motion.div {...fadeUp()}>
            <Label>Hvorfor vælge Apex Auto?</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Ingen overraskelser på regningen
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed">
              Du får altid en ærlig vurdering og en fast pris, før vi går i gang.
              Vi ringer, hvis vi finder noget uventet — og reparerer først, når du
              har sagt ja.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-4">
            {WHY.map((w, i) => (
              <motion.div key={w} {...fadeUp(i * 0.08)} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 flex items-start gap-3">
                <Check className="w-5 h-5 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-sm font-medium text-zinc-200 leading-relaxed">{w}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="ax-vaerksted" className="py-20 sm:py-28" data-testid="apex-about">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid gap-12 lg:grid-cols-2 items-center">
          <motion.div {...fadeUp(0.15)} className="rounded-xl overflow-hidden aspect-[4/3] bg-zinc-800 order-2 lg:order-1">
            <img
              src="https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Mekaniker i arbejde hos Apex Auto"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </motion.div>
          <motion.div {...fadeUp()} className="order-1 lg:order-2">
            <Label>Værkstedet</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Moderne udstyr, gammeldags stolthed
            </h2>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed">
              Apex Auto er et lokalt værksted med moderne diagnoseudstyr og
              mekanikere, der brænder for biler. Vi behandler din bil, som var det
              vores egen — og forklarer alt i et sprog, du forstår.
            </p>
          </motion.div>
        </div>
      </section>

      <section id="ax-anmeldelser" className="py-20 sm:py-28 bg-zinc-900/50" data-testid="apex-reviews">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()}>
            <Label>Kunderne siger</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Tillid bygget bil for bil</h2>
            <p className="mt-3 text-sm text-zinc-500">Fiktive demoanmeldelser — vist som layouteksempel.</p>
          </motion.div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <motion.figure key={r.name} {...fadeUp(i * 0.1)} className="bg-zinc-900 border border-zinc-800 rounded-xl p-7">
                <div className="flex gap-1 mb-4" aria-label="5 ud af 5 stjerner">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-red-500 text-red-500" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="text-base text-zinc-300 leading-relaxed">“{r.text}”</blockquote>
                <figcaption className="mt-5 text-sm font-medium text-zinc-500">{r.name}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <section id="ax-kontakt" className="py-20 sm:py-28 border-t border-zinc-800" data-testid="apex-contact">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid gap-12 lg:grid-cols-2 items-start">
          <motion.div {...fadeUp()}>
            <Label>Kontakt & booking</Label>
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight">
              Book tid på værkstedet
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-400 max-w-lg">
              Ring eller skriv — vi finder en tid hurtigt og giver dig en fast pris,
              før arbejdet begynder.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+4578901234"
                data-testid="apex-phone-link"
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white font-semibold rounded-lg px-8 py-4 hover:bg-red-500 hover:-translate-y-0.5 transition-all duration-200"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                Ring 78 90 12 34
              </a>
              <a
                href="mailto:vaerksted@apexauto.dk"
                data-testid="apex-email-link"
                className="inline-flex items-center justify-center gap-2 border border-zinc-600 text-white font-medium rounded-lg px-8 py-4 hover:border-white hover:-translate-y-0.5 transition-all duration-200"
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                vaerksted@apexauto.dk
              </a>
            </div>
            <p className="mt-6 text-xs text-zinc-500">
              Demo — telefonnummer og e-mail tilhører ikke en rigtig virksomhed.
            </p>
          </motion.div>
          <motion.div {...fadeUp(0.15)} className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
            <h3 className="font-mono-accent text-xs uppercase tracking-[0.25em] text-red-500 mb-6">
              Åbningstider
            </h3>
            <div className="border-t border-zinc-800">
              {HOURS.map(([day, time]) => (
                <div key={day} className="flex items-center justify-between py-4 border-b border-zinc-800">
                  <span className="text-sm text-zinc-400">{day}</span>
                  <span className="text-sm font-semibold text-white">{time}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span className="flex items-center gap-2 font-bold tracking-[0.12em] text-zinc-300">
            <span className="w-6 h-6 rounded-md bg-red-600 text-white flex items-center justify-center">
              <Wrench className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
            APEX AUTO
          </span>
          <p className="text-center">Konceptprojekt lavet af Naveli — ikke en rigtig kunde.</p>
          <Link to="/" data-testid="apex-footer-back-link" className="inline-flex items-center gap-1 hover:text-white transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Tilbage til Naveli
          </Link>
        </div>
      </footer>
    </div>
  );
}
